import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DatabaseService } from '../../../services/database.service';
import { environment } from '../../../services/global-environment';
import { I18nService } from '../../../services/i18n.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TimelineViewComponent } from '../timeline-view/timeline-view.component';
import { DynamicFormComponent, FormFieldConfig } from '../../dynamic-form/dynamic-form/dynamic-form.component';
import { FormConfigService } from '../../../services/form-config.service';
import { ApprovalFlowDisplayComponent, ApprovalStep } from '../../shared/approval-flow-display/approval-flow-display.component';
import { FileUploadService } from '../../../services/file-upload.service';
import { AuthService } from '../../../services/auth.service';
import { FormDisplayComponent } from '../../form-display/form-display.component';

// 時間軸數據介面定義
interface TimelineGroup {
  groupField: string;
  groupValue: string;
  periods: TimelinePeriod[];
}

interface TimelinePeriod {
  periodKey: string;  // 例如 "2023-01", "2023-W01", "2023-Q1" 等
  periodStart: Date;
  periodEnd: Date;
  count: number;
}

export interface Photo {
  fileName: string;
  fileUrl: string;
}

@Component({
  selector: 'app-showlist',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, TranslatePipe, DynamicFormComponent, ApprovalFlowDisplayComponent, FormDisplayComponent],
  templateUrl: './showlist.component.html',
  styleUrls: ['./showlist.component.scss']
})
export class ShowlistComponent implements OnInit, OnDestroy {

  @Input() formName: string = '';
  @Input() formId: string = '';
  @Input() parentFormId: string = ''; // 新增：父表單ID，用於子表單的新增記錄

  @Output() dataUpdated = new EventEmitter<void>(); // 資料更新事件發射器

  displayedColumns: string[] = [];
  data: any[] = [];
  companyid = '';
  currentUserId = ''; // 當前使用者ID，用於權限檢查

  // 欄位設定相關變數
  showColumnSettings: boolean = false;
  availableColumns: string[] = [];
  defaultColumns: string[] = [];
  columnSettings: {
    visibleColumns: string[];
    columnOrder: string[];
  } = {
    visibleColumns: [],
    columnOrder: []
  };

  // 編輯功能相關變數
  showEditDialog: boolean = false;
  editingItem: any = null;

  // 刪除功能相關變數
  showDeleteConfirm: boolean = false;
  deletingItem: any = null;

  // 新增記錄對話框相關變數
  showAddRecordDialog: boolean = false;

  // 編輯表單配置相關變數
  showEditFormDialog: boolean = false;
  editingFormConfig: any = null;
  editFormBuilderMode: 'ai' | 'manual' = 'ai';
  editFormAiMessage: string = '';
  editFormAiMessages: {role: 'user' | 'assistant', content: string}[] = [];
  isEditFormSending: boolean = false;
  editResponseId: string = '';

  // 載入狀態
  isLoading: boolean = false;

  // 搜尋字串
  searchQuery: string = '';
  // 多選搜尋欄位，預設空陣列（代表沒特定選擇，就搜尋全部欄位）
  searchColumns: string[] = [];

  // 視圖模式
  viewMode: 'table' | 'timeline' = 'table';

  // Dynamic Form 相關屬性
  originalFormConfig: FormFieldConfig[] = [];
  editFormConfig: FormFieldConfig[] = [];
  editingItemData: any = {};

  // 清理計時器
  private cacheCleanupInterval: any;

  // 子表單顯示相關屬性
  showSubform: boolean = false;
  subformId: string = '';
  subformName: string = '';
  subformDisplayTitle: string = ''; // 子表單顯示標題（使用欄位的 label）
  activeSubformData: any = null; // 儲存觸發子表單顯示的資料項目
  currentParentFormId: string = ''; // 新增：當前父表單ID，用於新增子記錄時使用

  // Dynamic API Table 相關屬性
  showDynamicApiTableDialog: boolean = false;
  dynamicApiTableDialogTitle: string = '';
  dynamicApiTableData: any[] = [];
  dynamicApiTableColumns: string[] = [];

  // Meeting Signature Collection 相關屬性
  showMeetingSignatureDialog: boolean = false;
  meetingSignatureDialogTitle: string = '';
  meetingSignatureData: any = {
    participants: [],
    signedCount: 0,
    totalCount: 0
  };

  // 表單查看相關屬性
  showViewForm: boolean = false;
  viewFormConfig: FormFieldConfig[] = [];
  viewFormData: any = {};
  viewFormName: string = '';

  constructor(
    private databaseService: DatabaseService,
    private http: HttpClient,
    private i18nService: I18nService,
    private formConfigService: FormConfigService,
    private fileUploadService: FileUploadService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    console.log('ShowlistComponent ngOnInit:', {
      formName: this.formName,
      formId: this.formId,
      parentFormId: this.parentFormId,
      isSubform: !!this.parentFormId
    });

    this.companyid = environment.companyid;
    this.currentUserId = this.authService.getUserInfo().userid; // 取得當前使用者ID
    if (!this.companyid) return;

    if (!this.formId) return;

    // 定期清理過期緩存（每5分鐘）
    this.cacheCleanupInterval = setInterval(() => {
      this.cleanExpiredCache();
    }, this.CACHE_DURATION);

    // 使用新的 API 取得表單容器資料
    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container`;
    const params = new URLSearchParams({
      companyid: this.companyid,
      componentname: this.formName,
      formid: this.formId
    });

    // 如果有 parentFormId，表示這是子表單，需要過濾資料_ts
    if (this.parentFormId) {
      params.append('parent_formid', this.parentFormId);
      console.log('載入子表單資料，父記錄ID:', this.parentFormId);
    }

    console.log("params",params);


    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.data = response.data;
            console.log('Received data:', this.data);
            // 取得所有欄位（排除系統欄位）
            this.availableColumns = this.getColumns();
            this.defaultColumns = [...this.availableColumns];

            // 載入欄位設定
            this.loadColumnSettings();

            // 確保新欄位被加入到設定中
            this.syncNewColumnsToSettings();

            this.loadData();

            // 載入原始表單配置
            this.loadOriginalFormConfig();

            // 預載入子表單資料筆數
            this.preloadSubformRecordCounts();
          } else {
            console.error('API response failed:', response);
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
        complete: () => {
          console.log('Data fetching complete');
        }
      });
  }

  loadData(): void {
    // 重新載入資料
    this.reloadFormData();
  }

  /**
   * 重新載入表單資料
   */
  private reloadFormData(): void {
    if (!this.companyid || !this.formId) return;

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container`;
    const params = new URLSearchParams({
      companyid: this.companyid,
      componentname: this.formName,
      formid: this.formId
    });

    // 如果有 parentFormId，表示這是子表單，需要過濾資料
    if (this.parentFormId) {
      params.append('parent_formid', this.parentFormId);
      console.log('重新載入子表單資料，父記錄ID:', this.parentFormId);
    }

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.data = response.data;
            console.log('重新載入資料完成:', this.data);

            // 重新載入後需要更新欄位設定
            this.availableColumns = this.getColumns();
            this.syncNewColumnsToSettings();
            this.applyColumnSettings();

            // 預載入子表單資料筆數
            this.preloadSubformRecordCounts();
          } else {
            console.error('重新載入資料 API 回應失敗:', response);
          }
        },
        error: (error) => {
          console.error('重新載入資料失敗:', error);
        }
      });
  }

  // 切換視圖模式
  switchViewMode(mode: 'table' | 'timeline'): void {
    this.viewMode = mode;
  }

  getUniqueGroupValues(groupField: string): string[] {
    const values = new Set<string>();

    this.data.forEach(item => {
      let value: string;

      if (item[groupField] && item[groupField].value !== undefined) {
        value = String(item[groupField].value);
      } else {
        value = String(item[groupField] || '');
      }

      values.add(value);
    });

    return Array.from(values).sort();
  }




  safeConvertToDate(value: any): Date | null {
    if (!value) return null;

    try {
      // 如果是數字或可以轉換為數字的字串
      if (typeof value === 'number' || !isNaN(Number(value))) {
        // 檢查位數判斷是秒還是毫秒格式的時間戳
        const timestamp = Number(value);
        if (timestamp < 10000000000) { // 秒級時間戳通常小於這個數
          return new Date(timestamp * 1000);
        } else {
          return new Date(timestamp);
        }
      } else if (typeof value === 'string') {
        // 如果是日期字串
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {
      console.error('Date conversion error:', error, value);
    }

    return null;
  }


  getItemPeriodKey(date: Date, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): string {
    switch (dimension) {
      case 'day':
        return this.formatDateForKey(date);

      case 'week':
        return `${date.getFullYear()}-W${this.getWeekNumber(date)}`;

      case 'month':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      case 'quarter':
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `${date.getFullYear()}-Q${quarter}`;

      case 'year':
        return `${date.getFullYear()}`;
    }
  }

  getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  getPeriodStartDate(periodKey: string, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): Date {
    switch (dimension) {
      case 'day':
        return new Date(periodKey);

      case 'week':
        const [yearStr, weekStr] = periodKey.split('-W');
        const year = parseInt(yearStr);
        const week = parseInt(weekStr);

        const firstDayOfYear = new Date(year, 0, 1);
        const dayOfWeek = firstDayOfYear.getDay();
        const daysToAdd = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);

        const firstWeekStart = new Date(firstDayOfYear);
        firstWeekStart.setDate(firstDayOfYear.getDate() + daysToAdd);

        const startDate = new Date(firstWeekStart);
        startDate.setDate(firstWeekStart.getDate() + (week - 1) * 7);

        return startDate;

      case 'month':
        const [yearM, monthM] = periodKey.split('-');
        return new Date(parseInt(yearM), parseInt(monthM) - 1, 1);

      case 'quarter':
        const [yearQ, quarterStr] = periodKey.split('-Q');
        const quarter = parseInt(quarterStr);
        const startMonth = (quarter - 1) * 3;
        return new Date(parseInt(yearQ), startMonth, 1);

      case 'year':
        return new Date(parseInt(periodKey), 0, 1);
    }
  }

  getPeriodEndDate(periodKey: string, dimension: 'day' | 'week' | 'month' | 'quarter' | 'year'): Date {
    const startDate = this.getPeriodStartDate(periodKey, dimension);
    const endDate = new Date(startDate);

    switch (dimension) {
      case 'day':
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'week':
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'month':
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'quarter':
        endDate.setMonth(endDate.getMonth() + 3);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'year':
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(0);
        endDate.setMonth(0);
        endDate.setHours(23, 59, 59, 999);
        break;
    }

    return endDate;
  }

  formatDateForKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getHeatmapColor(count: number, maxCount: number): string {
    if (maxCount <= 0 || count <= 0) return 'rgba(65, 105, 225, 0.1)';

    const intensity = Math.min(0.1 + (count / maxCount) * 0.8, 0.9);
    return `rgba(65, 105, 225, ${intensity})`;
  }

  // 需要過濾掉的欄位清單
  private readonly excludedColumns = [
    '_rid', '_self', '_etag', 'formid', '_attachments', '_ts','id', 'updatedAt',
    'companyid', 'assistantid', 'assistantname', 'componentname', 'parent_formid'
  ];

  // 需要過濾掉的欄位前綴清單
  private readonly excludedPrefixes = [
    'rich_text-display',
    'paragraph'
  ];

  // 取得資料中所有的欄位(在這邊直接設定那些欄位不顯示)
  getColumns(): string[] {
    if (this.data && this.data.length > 0) {
      const allKeys = new Set<string>();
      this.data.forEach(item => {
        Object.keys(item).forEach(key => {
          // 檢查是否在排除清單中
          const keyLower = key.toLowerCase();
          const isExcludedColumn = this.excludedColumns.includes(keyLower);

          // 檢查是否以排除前綴開頭
          const isExcludedPrefix = this.excludedPrefixes.some(prefix =>
            key.startsWith(prefix)
          );

          if (!isExcludedColumn && !isExcludedPrefix) {
            allKeys.add(key);
          }
        });
      });
      return Array.from(allKeys);
    }
    return [];
  }

  // 取得欄位的顯示名稱，優先使用 label，如果沒有則使用翻譯或欄位名稱
  getColumnDisplayName(column: string): string {
    if (!this.data || this.data.length === 0) {
      return this.getTranslatedColumnName(column);
    }

    // 找到第一個包含此欄位的資料項目
    for (const item of this.data) {
      if (item[column] && typeof item[column] === 'object' && item[column].label) {
        return item[column].label;
      }
    }

    // 如果沒有找到 label，返回翻譯後的欄位名稱或原欄位名稱
    return this.getTranslatedColumnName(column);
  }

  /**
   * 取得翻譯後的欄位名稱
   */
  private getTranslatedColumnName(column: string): string {
    // 定義需要翻譯的系統欄位（key 都用小寫）
    const systemFields: { [key: string]: string } = {
      'username': 'common.username',
      'createat': 'common.createAt',
      'created': 'common.createAt',
      'createdat': 'common.createAt',
      'created_at': 'common.createAt'
    };

    // 檢查是否為系統欄位（不區分大小寫）
    const translationKey = systemFields[column.toLowerCase()];
    if (translationKey) {
      return this.i18nService.translate(translationKey);
    }

    // 如果不是系統欄位，返回原欄位名稱
    return column;
  }

  // 根據 searchQuery 與 searchColumns 過濾資料
  get filteredData(): any[] {
    if (!this.searchQuery.trim()) {
      return this.data;
    }
    const lowerQuery = this.searchQuery.toLowerCase();

    if (this.searchColumns.length > 0) {
      return this.data.filter(item =>
        this.searchColumns.some(col =>
          String(item[col] || '').toLowerCase().includes(lowerQuery)
        )
      );
    } else {
      return this.data.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(lowerQuery)
        )
      );
    }
  }

  getSelectedOptions(options: { name: string; value: string; selected: boolean }[] | any): string {
    // 檢查 options 是否為陣列
    if (!Array.isArray(options)) {
      // 如果不是陣列，嘗試解析為陣列（可能是字串或其他格式）
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            return options; // 如果解析後不是陣列，直接返回字串值
          }
        } catch {
          return options; // 解析失敗，直接返回原值
        }
      } else if (options === null || options === undefined) {
        return ''; // 空值返回空字串
      } else {
        return String(options); // 其他類型轉為字串返回
      }
    }

    // 確保陣列中的元素有正確的結構
    return options.filter((option: any) => option && option.selected === true)
                  .map((option: any) => option.value || option.name || String(option))
                  .join('; ');
  }

  // 新增方法：處理多選框的選中項目顯示
  getMultiCheckboxSelectedText(options: { option: string; selected: boolean; textValue?: string }[] | any): string {
    // 檢查 options 是否為陣列
    if (!Array.isArray(options)) {
      // 如果不是陣列，嘗試解析為陣列
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            return options; // 如果解析後不是陣列，直接返回字串值
          }
        } catch {
          return options; // 解析失敗，直接返回原值
        }
      } else if (options === null || options === undefined) {
        return ''; // 空值返回空字串
      } else {
        return String(options); // 其他類型轉為字串返回
      }
    }

    // 找出所有被選中的選項
    const selectedOptions = options.filter((option: any) => option && option.selected === true);

    // 將選中的選項轉換為顯示文字
    const displayTexts = selectedOptions.map((option: any) => {
      let text = option.option || option.name || String(option);

      // 處理底線替換邏輯
      if (option.textValue && option.textValue.trim() !== '') {
        // 如果選項文字中包含底線，且有textValue，則用textValue替換所有底線
        if (text.includes('_')) {
          text = text.replace(/_+/g, " [" + option.textValue + "] ");
        } else {
          // 如果沒有底線但有textValue，則按原邏輯加上文字值（適用於"其他"等選項）
          text += `: ${option.textValue}`;
        }
      }

      return text;
    });

    return displayTexts.join(', ');
  }

  toggleSearchColumn(col: string, isChecked: boolean) {
    if (isChecked) {
      if (!this.searchColumns.includes(col)) {
        this.searchColumns.push(col);
      }
    } else {
      this.searchColumns = this.searchColumns.filter(c => c !== col);
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
  }

  convertUnixToLocal(timestamp: any): Date | null {
    // 檢查空值或無效值
    if (!timestamp || timestamp === null || timestamp === undefined) {
      return null;
    }

    // 轉換為字串進行檢查
    const timestampStr = String(timestamp);
    if (timestampStr.trim() === '' || timestampStr === '0') {
      return null;
    }

    const numTimestamp = Number(timestampStr);
    // 檢查是否為有效數字且不為 0
    if (isNaN(numTimestamp) || numTimestamp === 0) {
      return null;
    }

    return new Date(numTimestamp * 1000);
  }

  parsePhotos(photos: Photo[] | null): Photo[] {
    return photos ?? [];
  }

  viewImageFullSize(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  }

  getGoogleMapsUrl(location: string): string {
    const parts = location.split(',');
    if (parts.length === 2) {
      const lat = parts[0].trim();
      const lng = parts[1].trim();
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
    return '#';
  }

  // 檔案查看方法
  viewFile(file: any): void {
    console.log("file", file);

    // 檢查是否有 blobName
    if (file.blobName) {
      // 使用 FileUploadService 產生瀏覽連結
      this.fileUploadService.generateViewUrl(file.blobName).subscribe({
        next: (response: any) => {
          if (response.success && response.data.viewUrl) {
            window.open(response.data.viewUrl, '_blank');
          } else {
            console.warn('無法產生檔案瀏覽連結:', response);
            alert(this.i18nService.translate('showlist.file_link_generate_error'));
          }
        },
        error: (error) => {
          console.error('產生檔案瀏覽連結失敗:', error);
          alert(this.i18nService.translate('showlist.file_link_generate_failed'));
        }
      });
    } else {
      // 回退到原有的邏輯，支援已有 viewUrl 或 downloadUrl 的檔案
      const viewUrl = file.viewUrl || file.downloadUrl;

      if (viewUrl) {
        window.open(viewUrl, '_blank');
      } else {
        console.warn('檔案缺少必要資訊 (blobName 或 viewUrl):', file);
        alert(this.i18nService.translate('showlist.file_link_unavailable'));
      }
    }
  }

  onToggleSearchColumn(col: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.toggleSearchColumn(col, target.checked);
  }

  editItem(item: any) {
    console.log('Edit item:', item);

    // 檢查項目是否有 id 欄位（必須有才能更新）
    if (!item.id) {
      alert(this.i18nService.translate('showlist.edit_missing_id'));
      return;
    }

    this.editingItem = JSON.parse(JSON.stringify(item)); // 深度複製避免直接修改原始資料

    // 生成編輯表單配置
    this.editFormConfig = this.generateEditFormConfig(this.editingItem);

    // 準備初始資料
    this.editingItemData = this.prepareInitialData(this.editingItem);

    console.log('Edit form config:', this.editFormConfig);
    console.log('Initial data:', this.editingItemData);

    this.showEditDialog = true;
  }

  // 處理編輯項目的資料格式，確保日期時間欄位能在表單中正確顯示
  processEditingItemForDisplay(): void {
    if (!this.editingItem) return;

    // 只處理可編輯的欄位（排除系統欄位）
    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = this.editingItem[column];

      if (value && typeof value === 'object' && value.type) {
        if (value.type === 'datetime') {
          // 將 Unix 時間戳轉換為 datetime-local 格式
          if (value.value && (typeof value.value === 'number' || !isNaN(Number(value.value)))) {
            const date = new Date(Number(value.value) * 1000);
            // 格式化為 YYYY-MM-DDTHH:mm（datetime-local 所需格式）
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            this.editingItem[column].displayValue = `${year}-${month}-${day}T${hours}:${minutes}`;
          } else {
            // 如果沒有值或解析失敗，設定為空字串
            this.editingItem[column].displayValue = '';
          }
        } else if (value.type === 'date') {
          // 將日期格式轉換為 date input 所需格式
          if (value.value) {
            if (typeof value.value === 'string') {
              const date = new Date(value.value);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                this.editingItem[column].displayValue = `${year}-${month}-${day}`;
              } else {
                // 如果日期解析失敗，設定為空字串
                this.editingItem[column].displayValue = '';
              }
            } else {
              // 如果不是字串，設定為空字串
              this.editingItem[column].displayValue = '';
            }
          } else {
            // 如果沒有值，設定為空字串
            this.editingItem[column].displayValue = '';
          }
        }
      }
    });
  }

  // 取得可編輯的欄位（排除系統欄位）
  getEditableColumns(): string[] {
    return this.displayedColumns.filter(column => {
      // 排除一些不應該編輯的欄位
      const nonEditableFields = ['createAt', 'createat', 'created', 'timestamp'];
      return !nonEditableFields.some(field => column.toLowerCase().includes(field.toLowerCase()));
    });
  }

  // 檢查是否有操作權限（可以在此處添加權限邏輯）
  hasEditPermission(): boolean {
    // 這裡可以根據使用者角色或權限來決定是否允許編輯
    // 暫時返回 true，後續可以根據需求調整
    return true;
  }

  hasDeletePermission(): boolean {
    // 這裡可以根據使用者角色或權限來決定是否允許刪除
    // 暫時返回 true，後續可以根據需求調整
    return true;
  }

  // 處理編輯項目的資料格式，確保提交到資料庫的格式正確
  processEditingItemForSave(): void {
    if (!this.editingItem) return;

    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = this.editingItem[column];

      if (value && typeof value === 'object' && value.type) {
        if (value.type === 'datetime' && value.displayValue) {
          // 將 datetime-local 格式轉換回 Unix 時間戳
          const date = new Date(value.displayValue);
          if (!isNaN(date.getTime())) {
            this.editingItem[column].value = Math.floor(date.getTime() / 1000);
          }
          // 移除臨時的 displayValue
          delete this.editingItem[column].displayValue;
        } else if (value.type === 'date' && value.displayValue) {
          // 保存日期格式
          this.editingItem[column].value = value.displayValue;
          delete this.editingItem[column].displayValue;
        }
      }
    });
  }

  deleteItem(item: any) {
    console.log('Delete item:', item);

    // 檢查項目是否有 id 欄位（必須有才能刪除）
    if (!item.id) {
      alert(this.i18nService.translate('showlist.delete_missing_id'));
      return;
    }

    this.deletingItem = item;
    this.showDeleteConfirm = true;
  }

  // 關閉編輯對話框
  closeEditDialog(): void {
    this.showEditDialog = false;
    this.editingItem = null;
  }

  // 關閉刪除確認對話框
  closeDeleteConfirm(): void {
    this.showDeleteConfirm = false;
    this.deletingItem = null;
  }

  // 確認刪除
  confirmDelete(): void {
    if (!this.deletingItem) return;

    this.isLoading = true;

    // 呼叫新的 API 進行軟刪除
    const apiUrl = `${environment.apiUrl}/api/form-manager/delete_record`;
    const requestBody = {
      companyid: this.companyid,
      id: this.deletingItem.id
    };

    this.http.post(apiUrl, requestBody).subscribe({
      next: (response: any) => {
        console.log('Delete successful:', response);

        if (response.success) {
          // 從本地資料中移除
          this.data = this.data.filter(item => item.id !== this.deletingItem.id);

          // 關閉對話框
          this.closeDeleteConfirm();

          // 發射資料更新事件
          this.dataUpdated.emit();

          // 顯示成功訊息
          alert(response.message || this.i18nService.translate('showlist.delete_success'));
        } else {
          console.error('API 回應失敗:', response);
          alert(response.message || this.i18nService.translate('showlist.delete_failed'));
        }
      },
      error: (error) => {
        console.error('Delete failed:', error);

        // 根據錯誤狀態碼顯示適當的錯誤訊息
        let errorMessage = this.i18nService.translate('showlist.delete_failed');
        if (error.status === 400) {
          errorMessage = this.i18nService.translate('showlist.update_error_bad_request');
        } else if (error.status === 404) {
          errorMessage = this.i18nService.translate('showlist.update_error_not_found');
        } else if (error.status === 403) {
          errorMessage = this.i18nService.translate('showlist.update_error_forbidden');
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(errorMessage);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  isObject(val: any): boolean {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }



  // 處理簽核流程顯示
  parseApprovalFlow(approvalFlowValue: any): any[] {
    if (!approvalFlowValue) return [];

    try {
      // 如果已經是陣列，直接返回
      if (Array.isArray(approvalFlowValue)) {
        return approvalFlowValue;
      }

      // 如果是字串，嘗試解析 JSON
      if (typeof approvalFlowValue === 'string') {
        return JSON.parse(approvalFlowValue);
      }

      // 如果是物件且有 value 屬性 - 模仿 form-approval-list 的處理邏輯
      if (approvalFlowValue.value) {
        const approvalData = approvalFlowValue.value;

        // 檢查雙層巢狀結構 (approvalData.value?.steps) - 這是關鍵修復
        if (approvalData.value?.steps) {
          return approvalData.value.steps;
        }
        // 檢查單層結構 (approvalData.steps)
        else if (approvalData.steps) {
          return approvalData.steps;
        }
        // 如果 approvalData 直接是陣列
        else if (Array.isArray(approvalData)) {
          return approvalData;
        }
        // 如果 approvalFlowValue.value 是字串，嘗試解析
        else if (typeof approvalData === 'string') {
          return JSON.parse(approvalData);
        }
      }

      return [];
    } catch (error) {
      console.error('解析簽核流程失敗:', error);
      return [];
    }
  }

  // 取得簽核步驟的狀態顏色
  getApprovalStepColor(step: any): string {
    if (!step) return '#e9ecef'; // 預設灰色

    const status = step.status || 'pending';

    switch (status.toLowerCase()) {
      case 'approved':
        return '#28a745'; // 綠色 - 已核准
      case 'rejected':
        return '#dc3545'; // 紅色 - 已拒絕
      case 'in_progress':
        return '#ffc107'; // 黃色 - 進行中
      case 'pending':
      default:
        return '#6c757d'; // 灰色 - 待處理
    }
  }

  // 取得簽核步驟的狀態文字
  getApprovalStepStatusText(step: any): string {
    if (!step) return this.i18nService.translate('approval.status.unknown');

    const status = step.status || 'pending';

    switch (status.toLowerCase()) {
      case 'approved':
        return this.i18nService.translate('approval.status.approved');
      case 'rejected':
        return this.i18nService.translate('approval.status.rejected');
      case 'in_progress':
        return this.i18nService.translate('approval.status.in_progress');
      case 'pending':
      default:
        return this.i18nService.translate('approval.status.pending');
    }
  }

  // 建立簽核步驟的 tooltip 內容
  getApprovalStepTooltip(step: any): string {
    if (!step) return '';

    const stepName = step.stepName || step.name || this.i18nService.translate('approval.step.unnamed_step');
    const approverName = step.approverName || step.userName || step.approver?.userName || this.i18nService.translate('approval.step.unspecified_approver');
    const status = this.getApprovalStepStatusText(step);
    const actionDate = step.actionDate ? new Date(step.actionDate).toLocaleString() : '';

    let tooltip = `${this.i18nService.translate('approval.step.name')}: ${stepName}\n${this.i18nService.translate('approval.step.approver')}: ${approverName}\n${this.i18nService.translate('approval.step.status')}: ${status}`;

    if (actionDate) {
      tooltip += `\n${this.i18nService.translate('approval.step.process_time')}: ${actionDate}`;
    }

    if (step.comment) {
      tooltip += `\n${this.i18nService.translate('approval.step.comment')}: ${step.comment}`;
    }

    return tooltip;
  }

  /**
   * 將原始簽核流程資料轉換為 ApprovalFlowDisplayComponent 需要的格式
   */
  convertToApprovalFlowSteps(approvalFlowValue: any): ApprovalStep[] {
    const rawSteps = this.parseApprovalFlow(approvalFlowValue);

    if (!rawSteps || rawSteps.length === 0) {
      return [];
    }

    return rawSteps.map((step, index) => {
      // 決定步驟狀態 - 根據圖片中的顏色規則
      let status: 'pending' | 'in_progress' | 'approved' | 'rejected' = 'pending';

      if (step.status) {
        // 如果有明確的狀態，使用它
        status = step.status;
      } else {
        // 預設邏輯：第一步設為進行中（黃色），其他為等待中（灰色）
        if (index === 0) {
          status = 'in_progress';
        } else {
          status = 'pending';
        }
      }

      // 處理簽核者名稱 - 支援多種格式
      let approverName = '無指定人員';
      if (step.approverName) {
        approverName = step.approverName;
      } else if (step.userName) {
        approverName = step.userName;
      } else if (step.approver?.userName) {
        approverName = step.approver.userName;
      } else if (step.approverList && Array.isArray(step.approverList) && step.approverList.length > 0) {
        // 處理簽核者列表
        approverName = step.approverList.map((approver: any) => approver.name || approver.id).join(', ');
      }

      return {
        stepName: step.stepName || step.name || `步驟 ${index + 1}`,
        approverName: approverName,
        status: status,
        comment: step.comment,
        actionDate: step.actionDate
      };
    });
  }

  /**
   * 取得簽核流程原始資料
   */
  getApprovalFlowData(approvalFlowValue: any): any[] {
    return this.parseApprovalFlow(approvalFlowValue);
  }

  /**
   * 簽核完成回調
   */
  onApprovalComplete(approvalInstance: any): void {
    console.log('簽核完成:', approvalInstance);

    // 重新載入資料以更新狀態
    this.loadData();

    // 發出資料更新事件
    this.dataUpdated.emit();
  }

  // 載入原始表單配置
  loadOriginalFormConfig(): void {
    this.formConfigService.getFormConfig(this.companyid, this.formId, this.formName)
      .subscribe({
        next: (config: FormFieldConfig[]) => {
          this.originalFormConfig = config;
          console.log('Original form config loaded:', this.originalFormConfig);
        },
        error: (error) => {
          console.error('Failed to load original form config:', error);
          this.originalFormConfig = [];
        }
      });
  }

  /**
   * 預載入所有子表單的資料筆數
   */
  private preloadSubformRecordCounts(): void {
    if (!this.data || this.data.length === 0) return;

    const subformRelations = new Set<string>();

    // 找出所有 subform-relation 類型的欄位和對應的父記錄
    this.data.forEach(item => {
      this.displayedColumns.forEach(column => {
        const value = item[column];
        if (value && typeof value === 'object' && value.type === 'subform-relation' && value.value && item.id) {
          // 建立子表單ID與父記錄ID的關聯字串
          const relationKey = `${value.value}|${item.id}`;
          subformRelations.add(relationKey);
        }
      });
    });

    // 為每個子表單-父記錄組合載入資料筆數
    subformRelations.forEach(relationKey => {
      const [formId, parentFormId] = relationKey.split('|');
      this.loadSubformRecordCount(formId, parentFormId);
    });

    console.log(`開始預載入 ${subformRelations.size} 個子表單-父記錄組合的資料筆數`);
  }

  // 生成編輯表單配置
  generateEditFormConfig(editingItem: any): FormFieldConfig[] {
    if (!this.originalFormConfig || this.originalFormConfig.length === 0) {
      console.warn('Original form config not available, using fallback');
      return this.generateFallbackFormConfig(editingItem);
    }

    const editableColumns = this.getEditableColumns();
    const editConfig: FormFieldConfig[] = [];

    editableColumns.forEach(column => {
      // 在原始配置中尋找對應的欄位
      const originalField = this.originalFormConfig.find(field =>
        field.name === column || field.id === column
      );

      if (originalField) {
        // 使用原始配置建立編輯欄位
        const editField: FormFieldConfig = {
          ...originalField,
          // 確保編輯時的必要屬性
          id: `edit_${column}`,
          name: column
        };

        // 為欄位設定初始值
        this.setInitialValueForField(editField, editingItem[column]);

        editConfig.push(editField);
      } else {
        // 如果在原始配置中找不到，建立基本的文字輸入欄位
        const basicField: FormFieldConfig = {
          type: 'text-input',
          label: this.getColumnDisplayName(column),
          id: `edit_${column}`,
          name: column,
          placeholder: `請輸入${this.getColumnDisplayName(column)}`
        };

        // 為基本欄位設定初始值
        this.setInitialValueForField(basicField, editingItem[column]);

        editConfig.push(basicField);
      }
    });

    return editConfig;
  }

  // 為欄位設定初始值
  setInitialValueForField(field: FormFieldConfig, value: any): void {
    // 添加 initialValue 屬性來攜帶初始值
    (field as any).initialValue = '';

    if (value && typeof value === 'object' && value.value !== undefined) {
      // 處理具有類型的複雜資料結構
      if (value.type === 'datetime' && value.value) {
        // 將 Unix 時間戳轉換為適當的格式
        const timestamp = typeof value.value === 'number' ? value.value : Number(value.value);
        if (!isNaN(timestamp)) {
          (field as any).initialValue = timestamp;
        } else {
          (field as any).initialValue = value.value;
        }
      } else {
        (field as any).initialValue = value.value;
      }
    } else {
      // 簡單值直接使用
      (field as any).initialValue = value || '';
    }
  }

  // 當原始配置不可用時的後備配置
  generateFallbackFormConfig(editingItem: any): FormFieldConfig[] {
    const editableColumns = this.getEditableColumns();
    const fallbackConfig: FormFieldConfig[] = [];

    editableColumns.forEach(column => {
      const value = editingItem[column];
      let fieldType: FormFieldConfig['type'] = 'text-input';

      // 根據資料結構推測欄位類型
      if (value && typeof value === 'object' && value.type) {
        switch (value.type) {
          case 'textarea':
            fieldType = 'textarea';
            break;
          case 'select':
            fieldType = 'select';
            break;
          case 'date':
            fieldType = 'date';
            break;
          case 'datetime':
            fieldType = 'datetime';
            break;
          case 'number-input':
            fieldType = 'number-input';
            break;
          case 'location':
            fieldType = 'location';
            break;
          case 'photo-upload':
            fieldType = 'photo-upload';
            break;
          case 'signature':
            fieldType = 'signature';
            break;
          case 'multi-checkbox':
            fieldType = 'multi-checkbox';
            break;
          default:
            fieldType = 'text-input';
        }
      }

      const fieldConfig: FormFieldConfig = {
        type: fieldType,
        label: this.getColumnDisplayName(column),
        id: `edit_${column}`,
        name: column,
        placeholder: `請輸入${this.getColumnDisplayName(column)}`
      };

      // 如果是選擇類型，添加選項
      if (value && value.options) {
        fieldConfig.options = value.options;
      }

      fallbackConfig.push(fieldConfig);
    });

    return fallbackConfig;
  }

  // 準備編輯的初始資料
  prepareInitialData(editingItem: any): any {
    const initialData: any = {};
    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      const value = editingItem[column];

      if (value && typeof value === 'object' && value.value !== undefined) {
        // 處理具有類型的複雜資料結構
        if (value.type === 'datetime' && value.value) {
          // 將 Unix 時間戳轉換為適當的格式
          const timestamp = typeof value.value === 'number' ? value.value : Number(value.value);
          if (!isNaN(timestamp)) {
            initialData[column] = timestamp;
          } else {
            initialData[column] = value.value;
          }
        } else {
          initialData[column] = value.value;
        }
      } else {
        // 簡單值直接使用
        initialData[column] = value;
      }
    });

    return initialData;
  }

  // 處理 Dynamic Form 提交的資料
  handleEditFormSubmit(formData: any): void {
    if (!this.editingItem) return;

    console.log('Received form data from Dynamic Form:', formData);

    // 將 Dynamic Form 的資料轉換回原始格式
    const updatedItem = { ...this.editingItem };
    const editableColumns = this.getEditableColumns();

    editableColumns.forEach(column => {
      if (formData.hasOwnProperty(column)) {
        const originalValue = this.editingItem[column];
        const newValue = formData[column];

        if (originalValue && typeof originalValue === 'object' && originalValue.type) {
          // 保留原始的類型結構，只更新值
          updatedItem[column] = {
            ...originalValue,
            value: newValue
          };

          // 特殊處理日期時間欄位
          if (originalValue.type === 'datetime' && newValue) {
            // Dynamic Form 可能回傳日期物件或時間戳
            if (newValue instanceof Date) {
              updatedItem[column].value = Math.floor(newValue.getTime() / 1000);
            } else if (typeof newValue === 'string') {
              const date = new Date(newValue);
              if (!isNaN(date.getTime())) {
                updatedItem[column].value = Math.floor(date.getTime() / 1000);
              }
            }
          }
        } else {
          // 簡單值直接更新
          updatedItem[column] = newValue;
        }
      }
    });

    // 更新編輯項目
    this.editingItem = updatedItem;

    // 執行原有的儲存邏輯
    this.performUpdate();
  }

  // 執行實際的更新操作
  performUpdate(): void {
    this.isLoading = true;

    // 準備API請求的資料
    const saveData = {
      ...this.editingItem
    };

    // 使用新的 API 更新資料到資料庫
    const url = `${environment.apiUrl}/api/form-manager/save_form_container`;
    this.http.post<any>(url, saveData).subscribe({
      next: (response) => {
        console.log('Update successful:', response);

        if (response.success) {
          // 更新本地資料
          const index = this.data.findIndex(item => item.id === this.editingItem.id);
          if (index !== -1) {
            this.data[index] = { ...this.editingItem };
          }

          // 關閉對話框
          this.closeEditDialog();

          // 發射資料更新事件
          this.dataUpdated.emit();

          // 顯示成功訊息
          alert(this.i18nService.translate('showlist.update_success'));
        } else {
          console.error('Update failed:', response.message || response.error);
          alert(this.i18nService.translate('showlist.update_failed', { error: response.message || response.error || '未知錯誤' }));
        }
      },
      error: (error) => {
        console.error('Update failed:', error);
        let errorMessage = this.i18nService.translate('showlist.update_error');

        if (error.error?.message) {
          errorMessage = this.i18nService.translate('showlist.update_failed', { error: error.error.message });
        } else if (error.status === 404) {
          errorMessage = this.i18nService.translate('showlist.update_error_not_found');
        } else if (error.status === 409) {
          errorMessage = this.i18nService.translate('showlist.update_error_conflict');
        } else if (error.status === 403) {
          errorMessage = this.i18nService.translate('showlist.update_error_forbidden');
        } else if (error.status === 400) {
          errorMessage = this.i18nService.translate('showlist.update_error_bad_request');
        }

        alert(errorMessage);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }


  // 子表單資料筆數緩存
  private subformRecordCountCache: { [key: string]: { count: number; timestamp: number } } = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分鐘緩存

  /**
   * 取得子表單資料筆數（同步方法，優先使用緩存）
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（必填）
   * @returns 資料筆數
   */
  getSubformRecordCount(formId: string, parentFormId: string): number {
    if (!formId || !parentFormId) {
      return 0;
    }

    // 檢查緩存，緩存鍵包含 parent_formid
    const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;
    const cached = this.subformRecordCountCache[cacheKey];
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.count;
    }

    // 緩存過期或不存在，非同步載入新資料
    this.loadSubformRecordCount(formId, parentFormId);

    // 返回緩存值或預設值
    return cached ? cached.count : 0;
  }

  /**
   * 非同步載入子表單資料筆數
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（必填）
   */
  private loadSubformRecordCount(formId: string, parentFormId: string): void {
    if (!formId || !this.companyid || !parentFormId) return;

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_container_count`;
    const params = new URLSearchParams({
      companyid: this.companyid,
      formid: formId,
      parent_formid: parentFormId
    });

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            // 更新緩存鍵，包含 parent_formid
            const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;
            this.subformRecordCountCache[cacheKey] = {
              count: response.data || 0,
              timestamp: Date.now()
            };

            console.log(`子表單 ${formId} 資料筆數已更新: ${response.data} (父記錄: ${parentFormId})`);
          } else {
            console.error('取得子表單資料筆數 API 回應失敗:', response);
          }
        },
        error: (error) => {
          console.error('取得子表單資料筆數失敗:', error);

          // 發生錯誤時，設定預設值到緩存，避免重複請求
          const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;
          this.subformRecordCountCache[cacheKey] = {
            count: 0,
            timestamp: Date.now()
          };
        }
      });
  }

  /**
   * 清理過期的緩存
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    Object.keys(this.subformRecordCountCache).forEach(key => {
      if ((now - this.subformRecordCountCache[key].timestamp) > this.CACHE_DURATION) {
        delete this.subformRecordCountCache[key];
      }
    });
  }

  /**
   * 手動清除特定子表單的緩存
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（可選，如果提供則只清除特定父記錄的緩存；否則清除所有相關緩存）
   */
  clearSubformCache(formId: string, parentFormId?: string): void {
    if (!formId || !this.companyid) return;

    if (parentFormId) {
      // 清除特定父記錄的緩存
      const cacheKey = `${this.companyid}_${formId}_${parentFormId}`;
      delete this.subformRecordCountCache[cacheKey];

      // 重新載入該特定父記錄的資料
      this.loadSubformRecordCount(formId, parentFormId);
    } else {
      // 清除所有與該 formId 相關的緩存
      const prefix = `${this.companyid}_${formId}`;
      Object.keys(this.subformRecordCountCache).forEach(key => {
        if (key.startsWith(prefix)) {
          delete this.subformRecordCountCache[key];
        }
      });

      // 不進行重新載入，因為沒有 parentFormId 無法載入
      console.log(`已清除所有與 formId: ${formId} 相關的緩存`);
    }
  }


  // 新增記錄相關方法
  createNewRecord(): void {
    this.showAddRecordDialog = true;
    // 阻止背景滾动
    document.body.style.overflow = 'hidden';
  }

  closeAddRecordDialog(): void {
    this.showAddRecordDialog = false;
    // 恢復背景滾動
    document.body.style.overflow = 'auto';
  }

  // 處理新記錄添加完成後的回調
  onRecordAdded(): void {
    this.closeAddRecordDialog();
    // 重新載入資料
    this.loadData();
    // 發射資料更新事件
    this.dataUpdated.emit();
  }


  // 從資料庫載入表單配置
  loadFormConfigFromDatabase(formName: string): void {
    // 建立查詢條件
    const filter: any = {
      companyid: this.companyid,
      formName: formName
    };

    this.databaseService.getDataByQuery(filter, 1, 'form-config')
      .subscribe({
        next: (result) => {
          console.log('載入的表單配置:', result);

          // 配置載入完成後，顯示對話框並觸發變更檢測
          setTimeout(() => {
            this.showEditFormDialog = true;
            document.body.style.overflow = 'hidden';
          }, 10);
        },
        error: (error) => {
          console.error('載入表單配置失敗:', error);

          // 即使載入失敗，也要顯示對話框
          setTimeout(() => {
            this.showEditFormDialog = true;
            document.body.style.overflow = 'hidden';
          }, 10);
        }
      });
  }

  // 關閉編輯表單配置對話框
  closeEditFormDialog(): void {
    this.showEditFormDialog = false;
    this.editingFormConfig = null;
    this.resetEditFormState();
    document.body.style.overflow = 'auto';
  }

  // 重置編輯表單相關狀態
  resetEditFormState(): void {
    this.editFormBuilderMode = 'ai';
    this.editFormAiMessage = '';
    this.editFormAiMessages = [];
    this.isEditFormSending = false;
    this.editResponseId = '';
  }

  // 編輯表單 AI 相關方法
  onEditFormKeyDown(event: KeyboardEvent): void {
    // 按下 Enter 且沒有按住 Shift 發送消息
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.askEditFormAi();
    }
  }

  autoResizeEditFormInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  askEditFormAi(): void {
    console.log("askEditFormAi");

    if (!this.editFormAiMessage.trim()) {
      return;
    }

    // 添加用戶消息到列表
    this.editFormAiMessages.push({
      role: 'user',
      content: this.editFormAiMessage
    });

    // 清空輸入框
    const message = this.editFormAiMessage;
    this.editFormAiMessage = '';

    // 設置發送狀態
    this.isEditFormSending = true;

    // 模擬 AI 回應（這裡可以實際調用 AI 服務）
    setTimeout(() => {
      this.editFormAiMessages.push({
        role: 'assistant',
        content: `我了解您想要修改表單「${this.formName}」。目前這是一個簡化版本的編輯功能。您的請求：${message}`
      });

      this.isEditFormSending = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    // 清理計時器
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
    }

    // 清理所有緩存
    this.subformRecordCountCache = {};

    // 恢復頁面滾動（如果有打開的對話框）
    document.body.style.overflow = 'auto';

    // 關閉欄位設定對話框
    this.showColumnSettings = false;
  }

  /**
   * 載入子表單
   * @param subformId 子表單ID
   * @param parentItem 觸發顯示的父表單資料項目
   * @param columnName 觸發的欄位名稱，用於取得 label
   */
  loadSubform(subformId: string, parentItem?: any, columnName?: string): void {
    if (!subformId || !subformId.trim()) {
      console.warn('子表單ID為空，無法載入');
      return;
    }

    console.log('載入子表單:', subformId, '來源項目:', parentItem, '欄位名稱:', columnName);

    // 取得欄位的 label 作為子表單顯示標題
    let displayTitle = subformId; // 預設使用 subformId
    if (parentItem && columnName) {
      // 優先使用原本的欄位名稱（通過 getColumnDisplayName 獲得）
      displayTitle = this.getColumnDisplayName(columnName);

      // 如果 getColumnDisplayName 返回的是原始欄位名稱，再嘗試從資料中取得 label
      if (displayTitle === columnName) {
        const fieldData = parentItem[columnName];
        if (fieldData && fieldData.label) {
          displayTitle = fieldData.label;
        }
      }
    }

    // 先關閉子表單以確保重新載入
    this.showSubform = false;

    // 使用 setTimeout 確保 DOM 更新完成後再重新開啟
    setTimeout(() => {
      // 設定子表單相關屬性
      this.subformId = subformId.trim();
      this.subformDisplayTitle = displayTitle; // 設定顯示標題
      this.activeSubformData = parentItem;
      this.showSubform = true;

      // 設定當前父表單ID，用於新增子記錄時使用
      if (parentItem && parentItem.id) {
        this.currentParentFormId = parentItem.id;
      } else {
        this.currentParentFormId = '';
      }

      // 呼叫 API 取得表單名稱
      this.loadSubformName(subformId.trim());

      // 滾動到子表單位置
      setTimeout(() => {
        const subformElement = document.getElementById('subform-container');
        if (subformElement) {
          subformElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }, 0);
  }

  /**
   * 關閉子表單
   */
  closeSubform(): void {
    this.showSubform = false;
    this.subformId = '';
    this.subformName = '';
    this.subformDisplayTitle = '';
    this.activeSubformData = null;
    this.currentParentFormId = ''; // 清空父表單ID
  }

  /**
   * 子表單資料更新處理
   */
  onSubformDataUpdated(): void {
    console.log('子表單資料已更新');

    // 清除相關的子表單資料筆數緩存，以便重新載入最新筆數
    if (this.subformId && this.currentParentFormId) {
      // 清除特定父記錄的子表單緩存
      this.clearSubformCache(this.subformId, this.currentParentFormId);
    } else if (this.subformId) {
      // 如果沒有 currentParentFormId，清除所有相關緩存
      this.clearSubformCache(this.subformId);
    }

    // 重新載入當前表單的資料（支援 parentFormId 過濾）
    this.reloadFormData();

    // 發射資料更新事件，通知父組件
    this.dataUpdated.emit();
  }

  // === 欄位設定相關方法 ===

  /**
   * 開啟欄位設定對話框
   */
  openColumnSettings(): void {
    this.showColumnSettings = true;
    // 可選：防止背景滾動
    document.body.style.overflow = 'hidden';

    // 為無障礙性設定焦點
    setTimeout(() => {
      const dialog = document.querySelector('.column-settings-dialog h3') as HTMLElement;
      if (dialog) {
        dialog.focus();
      }
    }, 100);
  }

  /**
   * 關閉欄位設定對話框
   */
  closeColumnSettings(): void {
    this.showColumnSettings = false;
    // 恢復背景滾動
    document.body.style.overflow = 'auto';
  }

  /**
   * 載入欄位設定從 localStorage
   */
  loadColumnSettings(): void {
    try {
      const settingsKey = `showlist_${this.formName}_columnSettings`;
      const savedSettings = localStorage.getItem(settingsKey);

      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        this.columnSettings = {
          visibleColumns: parsedSettings.visibleColumns || [...this.defaultColumns],
          columnOrder: parsedSettings.columnOrder || [...this.defaultColumns]
        };
        console.log('載入欄位設定:', this.columnSettings);
      } else {
        // 如果沒有儲存的設定，使用預設值
        this.columnSettings = {
          visibleColumns: [...this.defaultColumns],
          columnOrder: [...this.defaultColumns]
        };
        console.log('使用預設欄位設定:', this.columnSettings);
      }

      // 應用設定到 displayedColumns
      this.applyColumnSettings();
    } catch (error) {
      console.error('載入欄位設定失敗:', error);
      // 發生錯誤時使用預設設定
      this.resetColumnSettings();
    }
  }

  /**
   * 儲存欄位設定到 localStorage
   */
  saveColumnSettings(): void {
    try {
      const settingsKey = `showlist_${this.formName}_columnSettings`;
      localStorage.setItem(settingsKey, JSON.stringify(this.columnSettings));
      console.log('欄位設定已儲存:', this.columnSettings);
    } catch (error) {
      console.error('儲存欄位設定失敗:', error);
    }
  }

  /**
   * 應用欄位設定到 displayedColumns
   */
  applyColumnSettings(): void {
    // 根據順序和可見性設定 displayedColumns
    // 注意：actions 欄位不在 availableColumns 中，所以會自動保持在適當位置
    this.displayedColumns = this.columnSettings.columnOrder.filter(column =>
      this.columnSettings.visibleColumns.includes(column) &&
      this.availableColumns.includes(column)
    );
  }

  /**
   * 重置欄位設定為預設值
   */
  resetColumnSettings(): void {
    this.columnSettings = {
      visibleColumns: [...this.defaultColumns],
      columnOrder: [...this.defaultColumns]
    };
    this.applyColumnSettings();
    this.saveColumnSettings();
  }

  /**
   * 切換欄位顯示狀態
   */
  toggleColumnVisibility(column: string): void {
    const index = this.columnSettings.visibleColumns.indexOf(column);
    if (index > -1) {
      this.columnSettings.visibleColumns.splice(index, 1);
    } else {
      this.columnSettings.visibleColumns.push(column);
    }
  }

  /**
   * 檢查欄位是否可見
   */
  isColumnVisible(column: string): boolean {
    return this.columnSettings.visibleColumns.includes(column);
  }

  /**
   * 拖拉排序欄位
   */
  dropColumn(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.columnSettings.columnOrder, event.previousIndex, event.currentIndex);
    }
  }

  /**
   * 套用欄位設定
   */
  applyColumnSettingsAndClose(): void {
    this.applyColumnSettings();
    this.saveColumnSettings();
    this.closeColumnSettings();
  }

  /**
   * 取消欄位設定變更
   */
  cancelColumnSettings(): void {
    // 重新載入設定（放棄變更）
    this.loadColumnSettings();
    this.closeColumnSettings();
  }

  /**
   * 取得已排序的可用欄位列表（用於設定介面顯示）
   */
  getOrderedAvailableColumns(): string[] {
    // 按照當前順序顯示所有可用欄位
    const orderedColumns = this.columnSettings.columnOrder.filter(col =>
      this.availableColumns.includes(col)
    );

    // 添加不在順序中的新欄位
    const missingColumns = this.availableColumns.filter(col =>
      !this.columnSettings.columnOrder.includes(col)
    );

    const result = [...orderedColumns, ...missingColumns];
    console.log('排序後的可用欄位:', result);
    return result;
  }

  /**
   * 同步新欄位到設定中
   */
  syncNewColumnsToSettings(): void {
    let hasChanges = false;

    // 新增不在設定中的欄位
    this.availableColumns.forEach(column => {
      if (!this.columnSettings.columnOrder.includes(column)) {
        this.columnSettings.columnOrder.push(column);
        this.columnSettings.visibleColumns.push(column);
        hasChanges = true;
      }
    });

    // 移除不存在的欄位
    this.columnSettings.columnOrder = this.columnSettings.columnOrder.filter(column =>
      this.availableColumns.includes(column)
    );
    this.columnSettings.visibleColumns = this.columnSettings.visibleColumns.filter(column =>
      this.availableColumns.includes(column)
    );

    if (hasChanges) {
      console.log('同步新欄位到設定:', this.columnSettings);
      this.saveColumnSettings();
      this.applyColumnSettings();
    }
  }

  /**
   * 載入子表單名稱
   * @param formId 子表單ID
   */
  private loadSubformName(formId: string): void {
    if (!formId || !this.companyid) {
      // 如果沒有 formId 或 companyid，使用 formId 作為預設名稱
      this.subformName = formId;
      return;
    }

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_form_name`;
    const params = new URLSearchParams({
      companyid: this.companyid,
      formid: formId
    });

    this.http.get(`${apiUrl}?${params.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data && response.data.formName) {
            this.subformName = response.data.formName;
            console.log(`成功取得子表單名稱: ${this.subformName} (ID: ${formId})`);
          } else {
            console.warn('取得子表單名稱 API 回應無效:', response);
            this.subformName = formId; // 使用 formId 作為後備名稱
          }
        },
        error: (error) => {
          console.error('取得子表單名稱失敗:', error);
          this.subformName = formId; // 使用 formId 作為後備名稱
        }
      });
  }

  /**
   * 處理預設顯示值 - 判斷是否為陣列且有 value 屬性
   * @param columnValue 欄位值
   * @returns 處理後的顯示值
   */
  getDisplayValue(columnValue: any): string {
    if (!columnValue) {
      return '';
    }

    // 如果是陣列且有元素
    if (Array.isArray(columnValue) && columnValue.length > 0) {
      // 檢查第一個元素是否有 value 屬性
      if (columnValue[0] && typeof columnValue[0] === 'object' && columnValue[0].hasOwnProperty('value')) {
        return columnValue[0].value || '';
      }
      // 如果是簡單陣列，返回第一個元素
      return String(columnValue[0]);
    }

    // 如果是物件且有 value 屬性
    if (typeof columnValue === 'object' && columnValue.hasOwnProperty('value')) {
      return columnValue.value || '';
    }

    // 其他情況直接返回字串化的值
    return String(columnValue);
  }

  /**
   * 取得 dynamic-api-table 的資料筆數
   * @param value dynamic-api-table 的值
   * @returns 資料筆數
   */
  getDynamicApiTableCount(value: any): number {
    if (!value) {
      return 0;
    }

    if (Array.isArray(value)) {
      return value.length;
    }

    return 0;
  }

  /**
   * 開啟 dynamic-api-table 資料顯示對話框
   * @param value dynamic-api-table 的值
   * @param columnName 欄位名稱
   */
  openDynamicApiTableDialog(value: any, columnName: string): void {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return;
    }



    this.dynamicApiTableDialogTitle = `${columnName} - 資料檢視`;
    this.dynamicApiTableData = value;

    // 從第一筆資料取得欄位名稱
    if (value.length > 0) {
      this.dynamicApiTableColumns = Object.keys(value[0]);
    } else {
      this.dynamicApiTableColumns = [];
    }

    this.showDynamicApiTableDialog = true;
  }

  /**
   * 關閉 dynamic-api-table 資料顯示對話框
   */
  closeDynamicApiTableDialog(): void {
    this.showDynamicApiTableDialog = false;
    this.dynamicApiTableDialogTitle = '';
    this.dynamicApiTableData = [];
    this.dynamicApiTableColumns = [];
  }

  /**
   * 取得會議簽名收集統計資訊
   */
  getMeetingSignatureCount(value: any): string {
    if (!value) return '0/0';

    try {
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      return `${data.signedCount || 0}/${data.totalCount || 0}`;
    } catch (error) {
      console.error('解析 meeting-signature-collection 資料失敗:', error);
      return '0/0';
    }
  }

  /**
   * 開啟會議簽名詳細資訊對話框
   */
  openMeetingSignatureDialog(value: any, title: string): void {
    try {
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      this.meetingSignatureData = {
        participants: data.participants || [],
        signedCount: data.signedCount || 0,
        totalCount: data.totalCount || 0
      };
      this.meetingSignatureDialogTitle = `${title} - 詳細簽名資訊`;
    } catch (error) {
      console.error('解析 meeting-signature-collection 資料失敗:', error);
      this.meetingSignatureData = {
        participants: [],
        signedCount: 0,
        totalCount: 0
      };
      this.meetingSignatureDialogTitle = `${title} - 詳細簽名資訊`;
    }

    this.showMeetingSignatureDialog = true;
  }

  /**
   * 關閉會議簽名詳細資訊對話框
   */
  closeMeetingSignatureDialog(): void {
    this.showMeetingSignatureDialog = false;
    this.meetingSignatureDialogTitle = '';
    this.meetingSignatureData = {
      participants: [],
      signedCount: 0,
      totalCount: 0
    };
  }

  /**
   * 格式化日期顯示
   */
  formatDate(date: string | Date): string {
    if (!date) return '';

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('格式化日期失敗:', error);
      return String(date);
    }
  }

  /**
   * 查看表單項目詳情
   */
  async viewItem(item: any): Promise<void> {
    console.log('查看項目:', item);

    try {
      // 設定基本資訊
      this.viewFormName = this.formName;
      this.viewFormData = item;

      // 檢查是否已有原始表單配置
      if (this.originalFormConfig && this.originalFormConfig.length > 0) {
        this.viewFormConfig = this.originalFormConfig;
      } else {
        // 如果沒有，嘗試載入表單配置
        await this.loadFormConfigForView();
      }

      // 顯示查看視窗
      this.showViewForm = true;

    } catch (error) {
      console.error('載入表單詳情失敗:', error);
      alert('載入表單詳情失敗，請稍後再試。');
    }
  }

  /**
   * 載入表單配置用於查看
   */
  private async loadFormConfigForView(): Promise<void> {
    if (!this.formId) {
      throw new Error('缺少表單ID');
    }

    const apiUrl = `${environment.apiUrl}/api/form-manager/get_specific_form_config`;
    const params = {
      companyid: this.companyid,
      formid: this.formId
    };

    try {
      const response = await this.http.get<any>(apiUrl, { params }).toPromise();
      if (response?.success && response?.data?.config) {
        this.viewFormConfig = response.data.config;
      } else {
        throw new Error('無法取得表單配置');
      }
    } catch (error) {
      console.error('載入表單配置失敗:', error);
      // 如果無法載入配置，使用預設的簡單配置
      this.viewFormConfig = [];
    }
  }

  /**
   * 關閉表單查看視窗
   */
  closeViewForm(): void {
    this.showViewForm = false;
    this.viewFormConfig = [];
    this.viewFormData = {};
    this.viewFormName = '';
  }
}


